import React from 'react';
import styled from 'styled-components';
import { Spin } from 'antd';
import { PlusOutlined, FileOutlined, FilePdfOutlined, FileWordOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button } from '@/components/ui/Button';
import Link from '@/components/ui/Link';
import { truncateFileName, resolveAttachmentUrl } from '@hooks/projects/projectHelpers';
import {
    isAttachmentImage,
    isAttachmentVideo,
    isAttachmentPdf,
    isAttachmentDoc,
    DETAIL_ATTACH_URL_LINK_STYLE,
    ATTACH_URLS_VISIBLE,
} from '@hooks/projects/detail/useProjectDetail';
import {
    DetailsTabAttachmentBlock,
    DetailsSectionLabel,
    ExternalLinkWrapRow,
    ExternalLinkWrapCell,
    DetailsAttachmentGridScroll,
    AttachmentCard,
    AttachmentAddCard,
} from '@/pages/inside/tenant/style';

/** Wraps a card — provides hover context for the small top-right delete button. */
const CardWrap = styled.div`
    position: relative;
    width: 120px;
    height: 120px;
    flex-shrink: 0;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
`;

/** Small delete button — top-right corner, revealed on CardWrap hover. No overlay. */
const DeleteCornerBtn = styled.button`
    position: absolute;
    top: 6px;
    right: 6px;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    border: 1.5px solid rgba(255, 255, 255, 0.5);
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.18s ease, background 0.15s, transform 0.15s;
    z-index: 4;
    padding: 0;
    line-height: 1;

    ${CardWrap}:hover & {
        opacity: 1;
        pointer-events: auto;
    }

    &:hover {
        background: rgba(239, 68, 68, 0.9);
        border-color: rgba(239, 68, 68, 0.95);
        transform: scale(1.12);
    }
`;

/**
 * Link pill — acts as the container itself (border + bg).
 * On hover the inline delete button slides in from the right.
 */
export const LinkWrap = styled.div`
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    min-width: 0;
    max-width: min(100%, 280px);
    width: max-content;
    box-sizing: border-box;
    padding: 4px 4px 4px 8px;
    border-radius: 40px;
    border: 1px solid
        ${({ theme }) =>
            theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'};
    background: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? 'transparent' : 'rgba(0,0,0,0.03)'};
    transition: border-color 0.18s, background 0.18s;
    gap: 4px;

    &:hover {
        border-color: ${({ theme }) =>
            theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.16)'};
        background: ${({ theme }) =>
            theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)'};
    }

    /* Suppress link underline inside the pill */
    a, a:hover, a:focus {
        text-decoration: none !important;
    }

    .link-delete-btn {
        width: 0;
        height: 22px;
        overflow: hidden;
        flex-shrink: 0;
        border: none;
        background: transparent;
        cursor: pointer;
        color: rgba(107, 114, 128, 0.75);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 13px;
        padding: 0;
        border-radius: 50%;
        opacity: 0;
        transition: width 0.22s ease, opacity 0.2s ease, color 0.15s;
    }

    &:hover .link-delete-btn {
        width: 22px;
        opacity: 1;
    }

    .link-delete-btn:hover {
        color: #ef4444;
    }
`;

export default function ProjectAttachmentsPanel({
    isClientRole,
    isInvitedCollaborator,
    handleAddAttachUrl,
    linkAttachments,
    attachUrlsExpanded,
    setAttachUrlsExpanded,
    fileAttachments,
    uploadAttachmentsPending,
    handleAttachmentAddClick,
    attachmentInputRef,
    handleAttachmentFileChange,
    handleAttachmentClick,
    onDeleteAttachment,
    onDeleteLink,
}) {
    const linkUrlsToShow = attachUrlsExpanded ? linkAttachments : linkAttachments.slice(0, ATTACH_URLS_VISIBLE);
    const hasMoreUrls = linkAttachments.length > ATTACH_URLS_VISIBLE;
    const canDelete = !isClientRole && !isInvitedCollaborator;

    return (
        <>
            <DetailsTabAttachmentBlock>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <DetailsSectionLabel style={{ marginBottom: 0 }}>Attach URL</DetailsSectionLabel>
                </div>
                <div style={{ marginBottom: 16 }}>
                    {linkUrlsToShow.length === 0 && !canDelete ? (
                        <span style={{ fontSize: 13, color: 'var(--ant-color-text-tertiary)' }}>No links</span>
                    ) : (
                        <ExternalLinkWrapRow>
                            {canDelete && (
                                <LinkWrap 
                                    style={{ 
                                        borderStyle: 'dashed', 
                                        borderColor: '#0284c7', 
                                        background: '#f0f9ff',
                                        cursor: 'pointer' 
                                    }}
                                    onClick={handleAddAttachUrl}
                                >
                                    <span style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 4, 
                                        fontSize: 13, 
                                        fontWeight: 600, 
                                        color: '#0284c7',
                                        paddingRight: 4
                                    }}>
                                        <PlusOutlined style={{ fontSize: 12 }} /> Add Link
                                    </span>
                                </LinkWrap>
                            )}
                            {linkUrlsToShow.map((a) => {
                                const href = a.url ?? a.href ?? '';
                                const displayTitle = a.title ?? a.name ?? href;
                                const linkId = a.id ?? href;
                                return (
                                    <LinkWrap key={linkId}>
                                        <Link
                                            href={href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title={displayTitle}
                                            style={{
                                                ...DETAIL_ATTACH_URL_LINK_STYLE,
                                                display: 'block',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                minWidth: 0,
                                                flex: '1 1 auto',
                                            }}
                                        >
                                            {displayTitle}
                                        </Link>
                                        {canDelete && (
                                            <button
                                                type="button"
                                                className="link-delete-btn"
                                                aria-label="Delete link"
                                                onClick={onDeleteLink ? (e) => { e.preventDefault(); e.stopPropagation(); onDeleteLink(a); } : undefined}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </LinkWrap>
                                );
                            })}
                        </ExternalLinkWrapRow>
                    )}
                    {hasMoreUrls && !attachUrlsExpanded && (
                        <Button
                            type="button"
                            $primary={false}
                            $size="12px"
                            $padding="4px 10px"
                            style={{ marginTop: linkUrlsToShow.length ? 10 : 0 }}
                            onClick={() => setAttachUrlsExpanded(true)}
                        >
                            See more
                        </Button>
                    )}
                    {hasMoreUrls && attachUrlsExpanded && (
                        <Button
                            type="button"
                            $primary={false}
                            $size="12px"
                            $padding="4px 10px"
                            style={{ marginTop: linkUrlsToShow.length ? 10 : 0 }}
                            onClick={() => setAttachUrlsExpanded(false)}
                        >
                            See less
                        </Button>
                    )}
                </div>
                <DetailsSectionLabel style={{ flexShrink: 0 }}>Attachment files</DetailsSectionLabel>
                <DetailsAttachmentGridScroll style={{ flex: 1, minHeight: 0 }}>
                    {/* Add card always at index 0 */}
                    {canDelete && (
                        <AttachmentAddCard
                            type="button"
                            title="Add attachment"
                            aria-label="Add attachment"
                            onClick={handleAttachmentAddClick}
                            disabled={uploadAttachmentsPending}
                        >
                            {uploadAttachmentsPending ? <Spin size="small" /> : <PlusOutlined style={{ fontSize: 24 }} />}
                        </AttachmentAddCard>
                    )}
                    {fileAttachments.map((att) => {
                        const isImage = isAttachmentImage(att);
                        const isVideo = isAttachmentVideo(att);
                        const isPdf = isAttachmentPdf(att);
                        const isDoc = isAttachmentDoc(att);
                        const rawUrl = att.url ?? att.fileUrl ?? att.path;
                        const url = rawUrl ? resolveAttachmentUrl(rawUrl) : rawUrl;
                        const cardId = att.id ?? att.url ?? att.name;
                        const iconLabel = (
                            <span
                                style={{
                                    fontSize: 10,
                                    textAlign: 'center',
                                    overflow: 'hidden',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    lineHeight: 1.2,
                                    wordBreak: 'break-word',
                                }}
                                title={att.name || att.title || 'File'}
                            >
                                {truncateFileName(att.name || att.title || 'File', 28)}
                            </span>
                        );
                        const iconWrap = (Icon, color) => (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, padding: 6 }}>
                                <Icon style={{ fontSize: 24, color: color || 'var(--ant-color-text-secondary)' }} />
                                {iconLabel}
                            </div>
                        );
                        let cardContent;
                        if (url && isImage) {
                            cardContent = <img src={url} alt={att.name || att.title || 'Attachment'} />;
                        } else if (url && isVideo) {
                            cardContent = <video src={url} preload="metadata" muted />;
                        } else if (isPdf) {
                            cardContent = iconWrap(FilePdfOutlined, '#e74c3c');
                        } else if (isDoc) {
                            cardContent = iconWrap(FileWordOutlined, '#2b579a');
                        } else {
                            cardContent = iconWrap(FileOutlined);
                        }
                        const resolvedHref = rawUrl ? resolveAttachmentUrl(rawUrl) : undefined;
                        const openInModal = isImage;
                        return (
                            <CardWrap key={cardId}>
                                <AttachmentCard
                                    as={openInModal ? 'button' : 'a'}
                                    type={openInModal ? 'button' : undefined}
                                    href={openInModal ? undefined : resolvedHref}
                                    target={openInModal ? undefined : '_blank'}
                                    rel={openInModal ? undefined : 'noopener noreferrer'}
                                    title={att.name ?? att.title}
                                    style={{ textDecoration: 'none', color: 'inherit' }}
                                    onClick={openInModal ? (e) => { e.preventDefault(); handleAttachmentClick(att); } : undefined}
                                >
                                    {cardContent}
                                </AttachmentCard>
                                {canDelete && (
                                    <DeleteCornerBtn
                                        type="button"
                                        aria-label="Delete attachment"
                                        onClick={onDeleteAttachment ? (e) => { e.preventDefault(); e.stopPropagation(); onDeleteAttachment(att); } : undefined}
                                    >
                                        <DeleteOutlined />
                                    </DeleteCornerBtn>
                                )}
                            </CardWrap>
                        );
                    })}
                </DetailsAttachmentGridScroll>
            </DetailsTabAttachmentBlock>
            <input
                ref={attachmentInputRef}
                type="file"
                accept="*/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleAttachmentFileChange}
            />
        </>
    );
}
